import React, { useState } from 'react';
import moment from 'moment';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  CalendarCheckIn01Icon,
  CalendarCheckOut01Icon,
  Calendar03Icon,
  FlashIcon,
  SquareLock02Icon
} from '@hugeicons/core-free-icons';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { ReservationSource } from '../../../../../shared/enums/reservation-souce.enum';
import { getBookingModeTag } from '../../../../../shared/services/helpers/stay-type.helper';
import {
  getReservationChannelLabel,
  getReservationSourceLabel
} from '../../../../../shared/services/helpers/reservation-source-channel.helper';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { updateReservationDates } from '../../../../shared/services/data.service';
import Tooltip from '../../../../../ui/Tooltip';
import { ChangeDatesModal } from '../change-dates-modal/change-dates-modal.component';

export interface StaySectionProps {
  data: {
    reservation: ReservationDetailsUiModel;
    canEditDates: boolean;
    showChangeDatesModal: boolean;
  };
  eventHandlers: {
    onRefresh: (id: string) => Promise<any>;
    onChangeDatesModalChange: (open: boolean) => void;
    onOpenSwitchRoomModal: () => void;
    onOpenSplitReservationModal: () => void;
  };
}

const outlineButton =
  'inline-flex items-center gap-[8px] px-[20px] py-[12px] rounded-[8px] border border-neutral-500 bg-neutral-0 text-[14px] font-gilroy-medium text-neutral-500 hover:bg-creams-200 disabled:border-gray-200 disabled:text-neutral-100 disabled:hover:bg-neutral-0 disabled:cursor-not-allowed';

const Fact = ({
  icon,
  title,
  children
}: {
  icon: typeof FlashIcon;
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex gap-[16px]">
    <HugeiconsIcon icon={icon} size={28} className="shrink-0 mt-[2px] text-neutral-500" />
    <div className="min-w-0">
      <div className="text-[16px] font-gilroy-medium text-neutral-500">{title}</div>
      <div className="text-[15px] text-neutral-300">{children}</div>
    </div>
  </div>
);

export const StaySection: React.FC<StaySectionProps> = ({
  data: { reservation, canEditDates, showChangeDatesModal },
  eventHandlers: {
    onRefresh,
    onChangeDatesModalChange,
    onOpenSwitchRoomModal,
    onOpenSplitReservationModal
  }
}) => {
  const [changeDatesLoading, setChangeDatesLoading] = useState(false);
  const [changeDatesError, setChangeDatesError] = useState<string | undefined>(undefined);

  const checkIn = moment(Number(reservation.checkInDate));
  const checkOut = moment(Number(reservation.checkOutDate));
  const nights = Math.max(
    checkOut.clone().startOf('day').diff(checkIn.clone().startOf('day'), 'days'),
    0
  );
  const today = moment().startOf('day');
  const daysToCheckIn = checkIn.clone().startOf('day').diff(today, 'days');
  const daysToCheckOut = checkOut.clone().startOf('day').diff(today, 'days');
  const guestsCount = reservation.guestIds?.length || 0;
  const nightsLabel = `${nights} ${nights === 1 ? 'night' : 'nights'}`;

  const checkInContext =
    daysToCheckIn > 0
      ? `in ${daysToCheckIn} ${daysToCheckIn === 1 ? 'day' : 'days'}`
      : daysToCheckIn === 0
      ? 'today'
      : undefined;
  const checkOutContext =
    daysToCheckIn < 0 && daysToCheckOut > 0
      ? `night ${-daysToCheckIn + 1} of ${nights}`
      : daysToCheckOut === 0
      ? 'today'
      : daysToCheckOut < 0
      ? `ended ${-daysToCheckOut} ${daysToCheckOut === -1 ? 'day' : 'days'} ago`
      : undefined;

  const scrollToGuests = () =>
    document.getElementById('reservation-guests')?.scrollIntoView({ behavior: 'smooth' });

  const bookingModeTag = getBookingModeTag(
    reservation.bookingMode,
    reservation.checkInDate,
    reservation.checkOutDate
  );
  const channelLabel = reservation.channel
    ? getReservationChannelLabel(reservation.channel)
    : getReservationSourceLabel(reservation.source);

  const isChannelManager = reservation.source === ReservationSource.CHANNEL_MANAGER;
  const changeDatesLockReason = isChannelManager
    ? `Updates only on ${reservation.channel ? reservation.channel.toUpperCase() : 'OTA directly'}`
    : !reservation.source
    ? 'Source not set'
    : undefined;

  const submitChangeDates = (newCheckInDate: number, newCheckOutDate: number) => {
    setChangeDatesLoading(true);
    setChangeDatesError(undefined);
    updateReservationDates(reservation.id, {
      checkInDate: newCheckInDate,
      checkOutDate: newCheckOutDate
    }).then((response) => {
      setChangeDatesLoading(false);
      if (isErrorInfo(response)) {
        setChangeDatesError(
          response.errorMessage ||
            'The selected dates are not available. Please choose different dates.'
        );
      } else {
        onChangeDatesModalChange(false);
        onRefresh(reservation.id);
      }
    });
  };

  const changeDatesButton = (
    <button
      type="button"
      className={outlineButton}
      disabled={!!changeDatesLockReason}
      onClick={() => {
        setChangeDatesError(undefined);
        onChangeDatesModalChange(true);
      }}
    >
      {changeDatesLockReason && <HugeiconsIcon icon={SquareLock02Icon} size={16} />}
      Change dates
    </button>
  );

  return (
    <>
      <div className="flex items-center gap-[12px] flex-wrap">
        <h2 className="m-0 text-[clamp(18px,1.5vw,22px)] font-gilroy-bold text-neutral-500">
          Stay
        </h2>
        {guestsCount === 0 && (
          <button
            type="button"
            onClick={scrollToGuests}
            className="px-[10px] py-[2px] rounded-full bg-secondary-50 text-secondary-700 text-[13px] font-gilroy-medium cursor-pointer"
          >
            No guests registered
          </button>
        )}
      </div>

      <div className="mt-[20px] max-w-[960px] grid sm:grid-cols-3 gap-[24px]">
        <Fact icon={CalendarCheckIn01Icon} title="Check-in">
          {checkIn.format('ddd, MMM D, YYYY')}
          {checkInContext && <span className="text-primary-700"> · {checkInContext}</span>}
        </Fact>
        <Fact icon={CalendarCheckOut01Icon} title="Check-out">
          {checkOut.format('ddd, MMM D, YYYY')}
          {checkOutContext && (
            <span className={daysToCheckOut < 0 ? 'text-neutral-300' : 'text-primary-700'}>
              {' '}
              · {checkOutContext}
            </span>
          )}
        </Fact>
        {bookingModeTag ? (
          <Tooltip title={bookingModeTag.tooltip}>
            <Fact
              icon={bookingModeTag.isInstant ? FlashIcon : Calendar03Icon}
              title={`${bookingModeTag.label} · ${nightsLabel}`}
            >
              {channelLabel}
            </Fact>
          </Tooltip>
        ) : (
          <Fact icon={Calendar03Icon} title={nightsLabel}>
            {channelLabel}
          </Fact>
        )}
      </div>

      <div className="mt-[24px] flex gap-[12px] flex-wrap">
        {canEditDates &&
          (changeDatesLockReason ? (
            <Tooltip title={changeDatesLockReason}>{changeDatesButton}</Tooltip>
          ) : (
            changeDatesButton
          ))}
        <button type="button" className={outlineButton} onClick={onOpenSwitchRoomModal}>
          {reservation.roomName ? 'Switch room' : 'Assign room'}
        </button>
        {canEditDates && (
          <button type="button" className={outlineButton} onClick={onOpenSplitReservationModal}>
            Shorten
          </button>
        )}
      </div>

      <ChangeDatesModal
        config={{ showModal: showChangeDatesModal }}
        data={{ reservation, isLoading: changeDatesLoading, errorMessage: changeDatesError }}
        eventHandlers={{
          closeHandler: () => {
            onChangeDatesModalChange(false);
            setChangeDatesError(undefined);
          },
          submitHandler: submitChangeDates
        }}
      />
    </>
  );
};
